import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Camera, Save, Clock, Loader2 } from 'lucide-react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../../firebase';
import { getSettings, updateSettings } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { useUserStore } from '../../store/userStore';

const storage = getStorage(app);

const IANA_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

export function CompanySettings() {
  const qc = useQueryClient();
  const { sessionConfig } = useUserStore();
  const fileRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
  });

  const [form, setForm] = useState({
    entityName: '',
    timezone: 'UTC',
    riskSchedule: 'nightly' as 'nightly' | 'weekly' | 'paused',
  });
  const [initialized, setInitialized] = useState(false);

  if (settings && !initialized) {
    setForm({
      entityName: settings.entityName,
      timezone: settings.timezone,
      riskSchedule: settings.riskSchedule ?? 'nightly',
    });
    setInitialized(true);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      setUploadError(null);
      let logoUrl: string | undefined;

      if (logoFile && sessionConfig?.entityId) {
        try {
          const path = `logos/${sessionConfig.entityId}/logo`;
          const fileRef2 = storageRef(storage, path);
          await uploadBytes(fileRef2, logoFile, { contentType: logoFile.type });
          logoUrl = await getDownloadURL(fileRef2);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setUploadError('Logo upload failed: ' + msg);
          throw err;
        }
      }

      return updateSettings({
        entityName: form.entityName,
        timezone: form.timezone,
        riskSchedule: form.riskSchedule,
        ...(logoUrl ? { logoUrl } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settings });
      setLogoFile(null);
      setLogoPreview(null);
    },
  });

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const displayLogo = logoPreview ?? settings?.logoUrl ?? null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Logo + name header */}
      <div className="flex items-center gap-4">
        <div className="relative">
          <div className="h-16 w-16 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 overflow-hidden flex items-center justify-center">
            {displayLogo ? (
              <img src={displayLogo} alt="Company logo" className="h-full w-full object-cover" />
            ) : (
              <span className="text-gray-400 text-2xl">🏢</span>
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-primary flex items-center justify-center border-2 border-white dark:border-bg-surface hover:bg-primary/80 transition-colors"
          >
            <Camera className="h-4 w-4 text-white" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{settings?.entityName}</p>
          <p className="text-meta text-gray-500 mt-0.5">
            {logoFile ? <span className="text-primary">New logo ready to save</span> : 'Company Account'}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-400 mb-1.5">
            Company Name
          </label>
          <input
            type="text"
            value={form.entityName}
            onChange={(e) => setForm((p) => ({ ...p, entityName: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder-gray-400"
          />
        </div>

        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            Timezone
          </label>
          <select
            value={form.timezone}
            onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50"
          >
            {IANA_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-400 mb-1.5">
            Risk Assessment Schedule
          </label>
          <select
            value={form.riskSchedule}
            onChange={(e) =>
              setForm((p) => ({ ...p, riskSchedule: e.target.value as typeof form.riskSchedule }))
            }
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50"
          >
            <option value="nightly">Nightly (midnight, Company Timezone)</option>
            <option value="weekly">Weekly (Sunday midnight, Company Timezone)</option>
            <option value="paused">Paused</option>
          </select>
          <p className="text-meta text-gray-400 mt-1">
            Scheduled runs use batch processing at reduced cost. Manual refresh is always available.
          </p>
        </div>

        {settings?.aiRefreshSchedule && (
          <div className="p-3 bg-gray-50 dark:bg-white/[0.03] rounded-lg border border-gray-200 dark:border-white/5">
            <p className="text-meta font-semibold text-gray-500 mb-0.5 uppercase tracking-wider">AI Refresh Schedule</p>
            <p className="text-meta text-gray-700 dark:text-gray-300 font-mono">{settings.aiRefreshSchedule}</p>
          </div>
        )}
      </div>

      {(uploadError || mutation.isError) && (
        <p className="text-meta text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-3">
          {uploadError ?? (mutation.error as Error).message}
        </p>
      )}

      {mutation.isSuccess && (
        <p className="text-meta text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/30 rounded-lg p-3">
          Settings saved successfully.
        </p>
      )}

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {mutation.isPending ? 'Saving…' : 'Save Changes'}
      </motion.button>
    </div>
  );
}
